import { useEffect, useRef } from 'react';
import { ExpandMore, Search } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, TextField, Typography, accordionClasses, accordionSummaryClasses } from '@mui/material';
import { isFalsy } from 'utility-types';
import { getBuisinessNameByChannel } from '@/api/appstore.api';
import { useIndicatorStore } from '@/stores/indicator.store';
import { useSearchStore } from '@/stores/search.store';
import { useToastStore } from '@/stores/toast.store';
import { addressConverter, getBuisinessNameNextPage } from '@/utils/appstore.util';
import theme from '@/app/theme';

const SearchBar = ({ searchWord }: { searchWord?: string }) => {
  const searchInput = useRef<HTMLInputElement>();

  const { searchResults, setSerachResults } = useSearchStore((state) => state);

  const { setShowToast, setToastInfos } = useToastStore((state) => state);
  const setShowIndicator = useIndicatorStore((state) => state.setShowIndicator);

  let prevSearchWord = '';

  const searchAddresses = async () => {
    const _searchWord = (searchInput?.current as unknown as HTMLInputElement)?.value ?? '';

    if (!_searchWord || _searchWord === '' || _searchWord?.trim() === '') {
      setToastInfos({ text: '검색어를 입력해주세요.' });
      setShowToast(true);

      return;
    }

    if (prevSearchWord === _searchWord) {
      return;
    }

    const channelName = 'NAVER';

    setShowIndicator(true);

    const searchRes = await getBuisinessNameByChannel(channelName, {
      query: _searchWord,
      page: getBuisinessNameNextPage(_searchWord, prevSearchWord, channelName, searchResults)
    });

    if (searchRes) {
      const newChannelData = {
        NAVER: {
          ...searchRes,
          list: (searchRes.list ?? []).map((addressData) => addressConverter(addressData, channelName))
        }
      };
      if (newChannelData) {
        setSerachResults(newChannelData);
      }
      prevSearchWord = _searchWord;
    }

    setShowIndicator(false);
  };

  useEffect(() => {
    if (searchInput && searchInput.current && !isFalsy(searchWord)) {
      searchInput.current.value = searchWord;
      searchAddresses();
    }
  }, []);

  return (
    <Box component="div" sx={{ padding: '12px 0px', width: 1 }}>
      <TextField
        inputRef={searchInput}
        variant="outlined"
        fullWidth={true}
        size="medium"
        placeholder="병원 이름을 검색해주세요."
        slotProps={{
          input: {
            startAdornment: <Search sx={{ color: theme.palette.gray[500], marginRight: '8px' }} onClick={searchAddresses} />
          }
        }}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            searchAddresses();
          }
        }}
      />

      <Box
        sx={{
          marginTop: '12px',
          padding: '12px 16px',
          border: `1px solid ${theme.palette.blue[300]}`,
          backgroundColor: theme.palette.blue[100],
          borderRadius: '8px',
          width: 'fit-content',
          transition: 'all 2000ms ease-in'
        }}
      >
        <Accordion
          sx={{
            border: 'none',
            boxShadow: 'none',
            padding: 0,
            margin: 0,
            backgroundColor: 'transparent',

            [`&.${accordionClasses.root}`]: { width: 'fit-content' },
            [`&.${accordionClasses.root} .${accordionSummaryClasses.content}`]: {
              margin: 0,
              minHeight: 'auto'
            },
            [`&.${accordionClasses.root} .${accordionSummaryClasses.expanded}`]: {
              margin: 0,
              minHeight: 'auto'
            },
            ':before': {
              display: 'none'
            },
            [`&.MuiAccordion-root .MuiCollapse-root`]: {
              display: 'none'
            },
            [`&.MuiAccordion-root.${accordionClasses.expanded} .MuiCollapse-root`]: {
              display: 'block'
            }
          }}
          slotProps={{
            heading: {
              sx: {
                display: 'flex',
                width: 'fit-content'
              }
            }
          }}
        >
          <AccordionSummary
            sx={{
              padding: 0,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              minHeight: 0
            }}
            expandIcon={<ExpandMore color="primary" />}
          >
            <Typography variant="body1" color="primary" fontWeight="semibold">
              검색 결과에 찾으시는 병원이 없나요?
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '12px 0 0 0', margin: 0 }}>
            <Typography>
              병원명과 병원이 위치한 주소(예: 역삼동)를 함께 입력하면 더 정확한 검색 결과를 얻을 수 있습니다. 주소가 일치하는 병원이 검색되지 않으면 검색어를 수정해보세요.
              <br />
              예시) 메디치과 역삼동
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default SearchBar;
